import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Chip, Skeleton, Stack, Typography } from "@mui/material";
import { Search, Eye, Heart, Columns3, Filter, MousePointerClick, LayoutDashboard, Briefcase, CreditCard, Settings as SettingsIcon, Compass, UserCheck, Download, Send, MapPin, Building2, FileText, Globe } from "lucide-react";
import sh from "bundles/common/utils/sh";
import { SectionLabel, EmptyNote } from "bundles/Internal/components/platform_analytics/PlatformAnalyticsShared";

// Human labels for the FeatureEvent `feature` keys emitted across the recruiter SPA.
const FEATURE_LABELS = {
  recruiter_nav: "Navigation",
  dashboard: "Dashboard & stats",
  jobs: "Job offers",
  kanban: "Kanban board",
  cvtheque: "CVthèque",
  billing: "Billing / pricing",
  settings: "Settings",
  candidate_jobs: "Job search",
  public_jobs: "Public job page",
  companies: "Company search",
  candidate_dashboard: "Dashboard",
  candidate_resume: "Resume",
  candidate_applications: "Applications",
};

const FEATURE_ICONS = {
  recruiter_nav: Compass,
  dashboard: LayoutDashboard,
  jobs: Briefcase,
  kanban: Columns3,
  cvtheque: Search,
  billing: CreditCard,
  settings: SettingsIcon,
  candidate_jobs: Briefcase,
  public_jobs: Globe,
  companies: Building2,
  candidate_dashboard: LayoutDashboard,
  candidate_resume: FileText,
  candidate_applications: Columns3,
};

const ACTION_ICONS = {
  profile_revealed: UserCheck,
  cv_downloaded: Download,
  portfolio_downloaded: Download,
  file_downloaded: Download,
  searched: Search,
  candidate_viewed: Eye,
  favorites_filtered: Heart,
  filters_opened: Filter,
  add_to_kanban_opened: Columns3,
  detail_opened: Eye,
  applied: Send,
  map_used: MapPin,
};

// Exact label per feature:action pair. Anything unmapped falls through to the
// generic formatter below, so a newly added trackEvent call still reads sensibly.
const ACTION_LABELS = {
  "recruiter_nav:tab_dashboard": "Opened the Dashboard tab",
  "recruiter_nav:tab_applications": "Opened the Kanban tab",
  "recruiter_nav:tab_jobs": "Opened the Job offers tab",
  "recruiter_nav:tab_cv_theque": "Opened the CVthèque tab",
  "recruiter_nav:tab_messages": "Opened the Messages tab",
  "recruiter_nav:tab_invoices": "Opened the Invoices tab",
  "dashboard:stats_tab_overview": "Opened the Overview stats tab",
  "dashboard:stats_tab_metrics": "Opened the Metrics stats tab",
  "dashboard:period_changed": "Changed the stats period",
  "jobs:filter_selected": "Filtered the job offers list",
  "jobs:offer_detail_viewed": "Opened a job offer",
  "jobs:publish_modal_opened": "Opened the publish dialog",
  "kanban:board_viewed": "Opened the Kanban board",
  "kanban:manage_board_opened": "Opened the board settings",
  "cvtheque:searched": "Ran a CVthèque search",
  "cvtheque:candidate_viewed": "Opened a candidate profile",
  "cvtheque:favorites_filtered": "Filtered on saved candidates",
  "cvtheque:filters_opened": "Opened the CVthèque filters",
  "cvtheque:add_to_kanban_opened": "Opened add-to-Kanban",
  "cvtheque:profile_revealed": "Showed interest in a profile",
  "cvtheque:cv_downloaded": "Downloaded a CV",
  "cvtheque:portfolio_downloaded": "Downloaded a portfolio",
  "cvtheque:file_downloaded": "Downloaded a file",
  "billing:buy_credits_clicked": "Clicked buy credits",
  "billing:pricing_viewed": "Opened the pricing page",
  "candidate_jobs:searched": "Searched jobs",
  "candidate_jobs:detail_opened": "Opened a job offer",
  "candidate_jobs:applied": "Applied to a job",
  "candidate_jobs:filters_opened": "Opened the job filters",
  "candidate_jobs:map_used": "Opened the map view",
  "public_jobs:searched": "Searched the public job page",
  "companies:searched": "Searched companies",
  "candidate_dashboard:viewed": "Opened the dashboard",
  "candidate_resume:opened": "Opened the resume",
  "candidate_applications:list_viewed": "Viewed the applications list",
  "candidate_applications:board_viewed": "Viewed the applications board",
};

const humanizeAction = (feature = "", action = "") => {
  const mapped = ACTION_LABELS[`${feature}:${action}`];
  if (mapped) return mapped;
  if (feature === "settings" && action.startsWith("opened_")) {
    return `Opened the ${action.replace("opened_", "").replace(/_/g, " ")} settings`;
  }
  if (feature === "dashboard" && action.startsWith("stats_tab_")) {
    return `Opened the ${action.replace("stats_tab_", "")} stats tab`;
  }
  if ((feature === "recruiter_nav" || feature === "candidate_dashboard") && action.startsWith("tab_")) {
    return `Opened the ${action.replace("tab_", "").replace(/_/g, " ")} tab`;
  }
  return `${feature.replace(/_/g, " ")}: ${action.replace(/_/g, " ")}`;
};

// The CVthèque merges hard skills, soft skills and languages into one `skills`
// array before searching, so a language search would otherwise read as
// "Skills: english". Split them back out for display.
const LANGUAGE_SKILLS = ["french", "dutch", "english"];

// Outcome actions come from real records (CandidateInterest / CandidateDownload)
// rather than client telemetry.
const OUTCOME_ACTIONS = ["profile_revealed", "cv_downloaded", "portfolio_downloaded", "file_downloaded"];

const JOB_ACTIONS = ["detail_opened", "applied"];

// Actions that identify a specific candidate. candidate_viewed is telemetry (a click in
// the feed) rather than an outcome, but it still names who was opened.
const CANDIDATE_ACTIONS = [...OUTCOME_ACTIONS, "candidate_viewed"];

const FILTER_LABELS = {
  skills: "Skills",
  region: "Region",
  desired_sector: "Sector",
  availability: "Availability",
  experience_year: "Experience",
  highest_degree: "Degree",
  active_within: "Active within",
  region_radius_km: "Radius",
  radius_km: "Radius",
  locations: "Locations",
  only_favorites: "Saved candidates only",
  desired_locations: "Locations",
  regions: "Regions",
  offer_language: "Offer language",
  company_industries: "Industries",
  company_sub_sectors: "Sub-sectors",
  job_statuses: "Contract",
  company_size: "Company size",
  offer_advantage_keys: "Benefits",
  exclude_applied: "Hide applied offers",
  distance_km: "Radius",
  industry: "Industry",
  company_industry: "Industry",
  team_size: "Team size",
  city: "City",
  benefits: "Benefits",
};

const HIDDEN_FILTERS = ["desired_locations", "origin_lat", "origin_lng"];

const titleize = (value) =>
  String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const describeFilters = (filters) => {
  if (!filters || typeof filters !== "object") return [];
  const chips = [];

  Object.entries(filters).forEach(([key, value]) => {
    if (HIDDEN_FILTERS.includes(key)) return; // desired_locations duplicates `region`
    if (value == null) return;

    // Advanced filters arrive as a boolean or a bare number, not an array.
    if (key === "only_favorites" || key === "exclude_applied") {
      if (value) chips.push(FILTER_LABELS[key]);
      return;
    }
    if (key === "active_within") {
      chips.push(`${FILTER_LABELS.active_within}: ${value} days`);
      return;
    }
    if (key === "region_radius_km" || key === "distance_km" || key === "radius_km") {
      chips.push(`${FILTER_LABELS[key]}: ${value} km`);
      return;
    }
    if (!Array.isArray(value)) {
      chips.push(`${FILTER_LABELS[key] || titleize(key)}: ${value}`);
      return;
    }

    const vals = value.filter((v) => v != null);
    if (vals.length === 0) return;

    if (key === "locations") {
      const names = vals.map((v) => (typeof v === "object" ? (v.label || v.code) : v)).filter(Boolean);
      if (names.length) chips.push(`${FILTER_LABELS.locations}: ${names.map((n) => titleize(String(n).split(",")[0])).join(", ")}`);
      return;
    }

    if (key === "skills") {
      const languages = vals.filter((v) => LANGUAGE_SKILLS.includes(String(v).toLowerCase()));
      const skills = vals.filter((v) => !LANGUAGE_SKILLS.includes(String(v).toLowerCase()));
      if (languages.length) chips.push(`Languages: ${languages.map(titleize).join(", ")}`);
      if (skills.length) chips.push(`Skills: ${skills.join(", ")}`);
      return;
    }

    chips.push(`${FILTER_LABELS[key] || titleize(key)}: ${vals.join(", ")}`);
  });

  return chips;
};

const dateTimeFmt = new Intl.DateTimeFormat("fr-BE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

const dayFmt = new Intl.DateTimeFormat("fr-BE", { weekday: "short", day: "2-digit", month: "long", year: "numeric" });

const END_REASON_LABELS = {
  closed: "Closed",
  logged_out: "Logged out",
  away_timeout: "Away > 30 min",
  idle_timeout: "Idle > 30 min",
  browser_crashed: "Browser crashed",
  killed_in_background: "Browser crashed (tab killed in background)",
  no_end_received: "Browser crashed or connection lost",
  switched_tab: "Switched tab",
  refined: "Refined",
  cleared: "Cleared filters",
  left_tab: "Left the tab",
};

const formatSeconds = (total) => {
  const seconds = Math.max(0, Math.round(Number(total) || 0));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, "0")}m`;
  if (minutes > 0) return `${minutes}m ${String(seconds % 60).padStart(2, "0")}s`;
  return `${seconds}s`;
};

const formatDateTime = (value) => {
  if (!value) return "Unknown";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "Unknown" : dateTimeFmt.format(d);
};

const formatDay = (value) => {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "Unknown date" : dayFmt.format(d);
};

const dayKey = (value) => {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "unknown" : d.toDateString();
};

/**
 * Raw in-product activity log, straight from FeatureEvent rows.
 *
 * scope="recruiter" → strictly the one recruiter on the page.
 * scope="company"   → every recruiter in the company, each row attributed.
 */
const FeatureActivityPanel = ({ scope, recruiterId, companyId, candidateId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [featureFilter, setFeatureFilter] = useState("all");
  const [recruiterFilter, setRecruiterFilter] = useState("all");

  const isCompanyScope = scope === "company";
  const isCandidateScope = scope === "candidate";
  const targetId = isCompanyScope ? companyId : isCandidateScope ? candidateId : recruiterId;

  useEffect(() => {
    if (!targetId) return undefined;
    let cancelled = false;
    setLoading(true);
    const resource = isCompanyScope ? "companies" : isCandidateScope ? "candidates" : "recruiters";
    const url = `/internal/${resource}/${targetId}/feature_activity`;
    sh.get(url)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [targetId, isCompanyScope, isCandidateScope]);

  const events = data?.events || [];
  const summary = data?.summary || {};
  const recruiters = data?.recruiters || [];

  const visible = useMemo(() => {
    let rows = events;
    if (featureFilter === "searches") {
      rows = rows.filter((e) => e.action === "searched");
    } else if (featureFilter === "failed_searches") {
      rows = rows.filter((e) => e.action === "searched" && Number(e.properties?.result_count) === 0);
    } else if (featureFilter === "jobs_opened") {
      rows = rows.filter((e) => e.feature === "candidate_jobs" && e.action === "detail_opened");
    } else if (featureFilter === "applications") {
      rows = rows.filter((e) => e.action === "applied");
    } else if (featureFilter === "profiles_opened") {
      rows = rows.filter((e) => e.action === "candidate_viewed");
    } else if (featureFilter === "reveals") {
      rows = rows.filter((e) => e.action === "profile_revealed");
    } else if (featureFilter === "downloads") {
      rows = rows.filter((e) => e.action.endsWith("_downloaded"));
    } else if (featureFilter !== "all") {
      rows = rows.filter((e) => e.feature === featureFilter);
    }
    if (isCompanyScope && recruiterFilter !== "all") {
      rows = rows.filter((e) => String(e.recruiter_id) === String(recruiterFilter));
    }
    return rows;
  }, [events, featureFilter, recruiterFilter, isCompanyScope]);

  const grouped = useMemo(() => {
    const buckets = [];
    let current = null;
    visible.forEach((e) => {
      const key = dayKey(e.occurred_at);
      if (!current || current.key !== key) {
        current = { key, label: formatDay(e.occurred_at), rows: [] };
        buckets.push(current);
      }
      current.rows.push(e);
    });
    return buckets;
  }, [visible]);

  const featureChips = useMemo(() => {
    const byFeature = summary.by_feature || {};
    return Object.keys(FEATURE_LABELS)
      .filter((key) => byFeature[key])
      .map((key) => ({ key, label: FEATURE_LABELS[key], count: byFeature[key] }));
  }, [summary]);

  const scopeNote = isCompanyScope
    ? "Every tracked event for ALL recruiters in this company, newest first. Each row is attributed to the recruiter who produced it. God-view sessions are never recorded."
    : isCandidateScope
      ? "Every tracked event for this candidate, newest first, including searches made on public pages in the same browser tab before signing up. God-view sessions are never recorded."
      : "Every tracked event for THIS recruiter only, never their colleagues. Newest first. God-view sessions are never recorded.";

  return (
    <Box sx={{ mt: isCandidateScope ? 3 : 0 }}>
      <SectionLabel note={scopeNote}>
        {isCompanyScope ? "Activity log: all recruiters" : isCandidateScope ? "Activity log" : "Activity log: this recruiter only"}
      </SectionLabel>

      {/* Explicit scope banner: the two pages look alike, so state the scope on the page itself. */}
      <Box
        sx={{
          mt: 1,
          p: 1.25,
          borderRadius: "8px",
          border: "1px solid hsl(140, 8%, 88%)",
          backgroundColor: isCompanyScope ? "hsl(217 50% 96%)" : "hsl(150 30% 96%)",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <MousePointerClick size={14} style={{ color: isCompanyScope ? "hsl(217 70% 30%)" : "hsl(150, 38%, 17%)", flexShrink: 0 }} />
        <Typography sx={{ fontSize: 12.5, color: "hsl(150, 30%, 12%)", fontFamily: "Segoe UI Regular, sans-serif" }}>
          {isCompanyScope ? (
            <>
              Showing <strong>all {summary.recruiters_total ?? recruiters.length} recruiters</strong> in this company
              {summary.recruiters_active != null && <> · {summary.recruiters_active} with recorded activity</>}
            </>
          ) : isCandidateScope ? (
            <>Showing: searches, job offers opened and applications, in order</>
          ) : (
            <>Showing <strong>this recruiter only</strong>. Colleagues&rsquo; activity is excluded</>
          )}
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ mt: 1.5 }}>
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} height={42} sx={{ mb: 0.5, bgcolor: "hsl(140 8% 92%)" }} />
          ))}
        </Box>
      ) : (
        <>
          {/* Filters: counts live on the chips, so no separate stat cards. */}
          <Stack direction="row" sx={{ mt: 1.5, gap: 0.75, flexWrap: "wrap" }}>
            <Chip
              label={`All (${summary.total ?? 0})`}
              size="small"
              onClick={() => setFeatureFilter("all")}
              sx={chipSx(featureFilter === "all")}
            />
            <Chip
              label={`Searches (${summary.searches ?? 0})`}
              size="small"
              onClick={() => setFeatureFilter("searches")}
              sx={chipSx(featureFilter === "searches", "hsl(190 45% 30%)", "hsl(190 35% 93%)")}
            />
            {isCandidateScope ? (
              <>
                <Chip
                  label={`Failed searches (${summary.failed_searches ?? 0})`}
                  size="small"
                  onClick={() => setFeatureFilter("failed_searches")}
                  sx={chipSx(featureFilter === "failed_searches", "hsl(0 60% 38%)", "hsl(0 60% 95%)")}
                />
                <Chip
                  label={`Jobs opened (${summary.jobs_opened ?? 0})`}
                  size="small"
                  onClick={() => setFeatureFilter("jobs_opened")}
                  sx={chipSx(featureFilter === "jobs_opened", "hsl(190 45% 30%)", "hsl(190 35% 93%)")}
                />
                <Chip
                  label={`Applications (${summary.applications ?? 0})`}
                  size="small"
                  onClick={() => setFeatureFilter("applications")}
                  sx={chipSx(featureFilter === "applications", "hsl(150 38% 30%)", "hsl(150 30% 93%)")}
                />
              </>
            ) : (
              <>
                <Chip
                  label={`Profiles opened (${summary.profiles_opened ?? 0})`}
                  size="small"
                  onClick={() => setFeatureFilter("profiles_opened")}
                  sx={chipSx(featureFilter === "profiles_opened", "hsl(190 45% 30%)", "hsl(190 35% 93%)")}
                />
                <Chip
                  label={`Reveals (${summary.reveals ?? 0})`}
                  size="small"
                  onClick={() => setFeatureFilter("reveals")}
                  sx={chipSx(featureFilter === "reveals", "hsl(150 38% 30%)", "hsl(150 30% 93%)")}
                />
                <Chip
                  label={`Downloads (${summary.downloads ?? 0})`}
                  size="small"
                  onClick={() => setFeatureFilter("downloads")}
                  sx={chipSx(featureFilter === "downloads", "hsl(150 38% 30%)", "hsl(150 30% 93%)")}
                />
              </>
            )}
            {featureChips.map((f) => (
              <Chip
                key={f.key}
                label={`${f.label} (${f.count})`}
                size="small"
                onClick={() => setFeatureFilter(f.key)}
                sx={chipSx(featureFilter === f.key)}
              />
            ))}
          </Stack>

          {/* Recruiter filter, company scope only */}
          {isCompanyScope && recruiters.length > 1 && (
            <Stack direction="row" sx={{ mt: 1, gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
              <Typography sx={{ fontSize: 11, color: "hsl(150, 10%, 46%)", mr: 0.5 }}>Recruiter:</Typography>
              <Chip label="All" size="small" onClick={() => setRecruiterFilter("all")} sx={chipSx(recruiterFilter === "all")} />
              {recruiters.map((r) => (
                <Chip
                  key={r.id}
                  label={r.name || r.email || `#${r.id}`}
                  size="small"
                  onClick={() => setRecruiterFilter(String(r.id))}
                  sx={chipSx(String(recruiterFilter) === String(r.id))}
                />
              ))}
            </Stack>
          )}

          {summary.truncated && (
            <Typography sx={{ mt: 1, fontSize: 11.5, color: "hsl(150, 10%, 46%)" }}>
              Showing the {summary.returned} most recent of {summary.total} events.
            </Typography>
          )}

          {/* Log */}
          <Box sx={{ border: "1px solid hsl(140, 8%, 88%)", borderRadius: "0.75rem", backgroundColor: "#ffffff", mt: 1.5, p: 0, overflow: "hidden" }}>
            {grouped.length === 0 ? (
              <EmptyNote>No tracked activity for this selection</EmptyNote>
            ) : (
              grouped.map((day) => (
                <Box key={day.key}>
                  <Box
                    sx={{
                      px: 2,
                      py: 0.75,
                      backgroundColor: "hsl(140 8% 96%)",
                      borderTop: "1px solid hsl(140, 8%, 88%)",
                      borderBottom: "1px solid hsl(140, 8%, 88%)",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    <Typography sx={{ fontSize: 11, letterSpacing: "0.05em", textTransform: "uppercase", color: "hsl(150, 10%, 46%)", fontFamily: "Segoe UI Semibold, sans-serif" }}>
                      {day.label} · {day.rows.length} event{day.rows.length === 1 ? "" : "s"}
                    </Typography>
                  </Box>

                  {day.rows.map((e, index) => {
                    const isSearch = e.action === "searched";
                    const isFailedSearch = isSearch && Number(e.properties?.result_count) === 0;
                    const opensJob = JOB_ACTIONS.includes(e.action) && e.feature === "candidate_jobs";
                    const previous = day.rows[index - 1];
                    const startsNewSession =
                      previous && previous.session_id && e.session_id && previous.session_id !== e.session_id;
                    // Reveals and downloads are real records, not telemetry: they are the
                    // outcomes of a search, so they get the same highlighted treatment.
                    const isOutcome = OUTCOME_ACTIONS.includes(e.action) || e.action === "applied";
                    const namesCandidate = CANDIDATE_ACTIONS.includes(e.action);
                    const Icon = ACTION_ICONS[e.action] || FEATURE_ICONS[e.feature] || MousePointerClick;
                    const filterChips = isSearch ? describeFilters(e.properties?.filters) : [];

                    return (
                      <React.Fragment key={e.id}>
                        {startsNewSession && (
                          <Box sx={{ px: 2, py: 0.4, display: "flex", alignItems: "center", gap: 1, backgroundColor: "hsl(140 8% 98%)", borderBottom: "1px dashed hsl(140, 8%, 88%)" }}>
                            <Typography sx={{ fontSize: 10.5, letterSpacing: "0.05em", textTransform: "uppercase", color: "hsl(150, 10%, 46%)", fontFamily: "Segoe UI Semibold, sans-serif" }}>
                            New session
                            </Typography>
                          </Box>
                        )}
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "168px 1fr" },
                            gap: { xs: 0.5, sm: 1.5 },
                            px: 2,
                            py: 1.25,
                            borderBottom: "1px solid hsl(140, 8%, 88%)",
                            "&:last-of-type": { borderBottom: "none" },
                            backgroundColor: isFailedSearch ? "hsl(0 60% 98%)" : isSearch ? "hsl(190 40% 98%)" : isOutcome ? "hsl(150 30% 98%)" : "transparent",
                          }}
                        >
                          {/* Exact timestamp: the whole point of this log */}
                          <Typography
                            sx={{
                              fontSize: 11.5,
                              color: "hsl(150, 10%, 46%)",
                              fontVariantNumeric: "tabular-nums",
                              fontFamily: "Segoe UI Regular, sans-serif",
                              whiteSpace: "nowrap",
                              pt: 0.2,
                            }}
                          >
                            {formatDateTime(e.occurred_at)}
                          </Typography>

                          <Box sx={{ minWidth: 0 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                              <Icon size={13} style={{ color: isSearch ? "hsl(190 55% 30%)" : isOutcome ? "hsl(150 38% 30%)" : "hsl(150, 10%, 46%)", flexShrink: 0 }} />
                              <Typography sx={{ fontSize: 13, color: "hsl(150, 30%, 12%)", fontFamily: "Segoe UI Semibold, sans-serif" }}>
                                {humanizeAction(e.feature, e.action)}
                              </Typography>
                              {isCompanyScope && (
                                <Chip
                                  label={e.recruiter_name || `#${e.recruiter_id}`}
                                  size="small"
                                  sx={{ height: 18, fontSize: 10, borderRadius: "999px", backgroundColor: "hsl(217 45% 94%)", color: "hsl(217 70% 30%)" }}
                                />
                              )}
                              {e.engagement && (
                                <Chip
                                  label={`${formatSeconds(e.engagement.active_seconds)} active${e.engagement.away_seconds ? ` · ${formatSeconds(e.engagement.away_seconds)} away` : ""} · ${e.engagement.open ? "in progress" : END_REASON_LABELS[e.engagement.end_reason] || e.engagement.end_reason}`}
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: 10,
                                    borderRadius: "999px",
                                    backgroundColor: e.engagement.crashed ? "hsl(0 60% 93%)" : "hsl(150 20% 93%)",
                                    color: e.engagement.crashed ? "hsl(0 60% 38%)" : "hsl(150 30% 24%)",
                                  }}
                                />
                              )}
                            </Box>

                            {/* Search detail, in full */}
                            {isSearch && (
                              <Box sx={{ mt: 0.5 }}>
                                <Typography sx={{ fontSize: 12.5, color: "hsl(150, 30%, 12%)" }}>
                                  {e.properties?.query ? (
                                    <>Query: <strong>&ldquo;{e.properties.query}&rdquo;</strong></>
                                  ) : (
                                    <span style={{ color: "hsl(150, 10%, 46%)" }}>No text query</span>
                                  )}
                                  {e.properties?.result_count != null && !isFailedSearch && (
                                    <span style={{ color: "hsl(150, 10%, 46%)" }}>
                                      {" "}({e.properties.result_count} result{Number(e.properties.result_count) === 1 ? "" : "s"})
                                    </span>
                                  )}
                                </Typography>
                                {(filterChips.length > 0 || e.properties?.has_credit === false || isFailedSearch) && (
                                  <Stack direction="row" sx={{ mt: 0.5, gap: 0.5, flexWrap: "wrap" }}>
                                    {isFailedSearch && (
                                      <Chip label="No results" size="small" sx={{ height: 19, fontSize: 10.5, borderRadius: "999px", fontFamily: "Segoe UI Semibold, sans-serif", backgroundColor: "hsl(0 60% 93%)", color: "hsl(0 60% 38%)" }} />
                                    )}
                                    {e.properties?.has_credit === false && (
                                      <Chip label="No CV credit" size="small" sx={{ height: 19, fontSize: 10.5, borderRadius: "999px", backgroundColor: "hsl(30 45% 93%)", color: "hsl(30 60% 32%)" }} />
                                    )}
                                    {filterChips.map((label) => (
                                      <Chip key={label} label={label} size="small" sx={{ height: 19, fontSize: 10.5, borderRadius: "999px", backgroundColor: "hsl(190 35% 93%)", color: "hsl(190 45% 30%)" }} />
                                    ))}
                                  </Stack>
                                )}
                              </Box>
                            )}

                            {opensJob && (
                              e.properties?.job_offer_id ? (
                                <Box
                                  component="button"
                                  type="button"
                                  onClick={() => navigate(`/internal/job_offers/${e.properties.job_offer_id}`)}
                                  sx={{
                                    mt: 0.25,
                                    p: 0,
                                    border: "none",
                                    background: "none",
                                    display: "block",
                                    textAlign: "left",
                                    cursor: "pointer",
                                    fontSize: 12.5,
                                    fontFamily: "Segoe UI Semibold, sans-serif",
                                    color: "hsl(150 38% 30%)",
                                    "&:hover": { textDecoration: "underline" },
                                    "&:focus-visible": { outline: "2px solid hsl(150 38% 30%)", outlineOffset: 2 },
                                  }}
                                >
                                  {e.properties.job_title || `Job offer #${e.properties.job_offer_id}`}
                                  {e.properties.company_name && (
                                    <span style={{ color: "hsl(150, 10%, 46%)", fontFamily: "Segoe UI Regular, sans-serif" }}> · {e.properties.company_name}</span>
                                  )}
                                </Box>
                              ) : (
                                <Typography sx={{ mt: 0.25, fontSize: 12.5, color: "hsl(150, 10%, 46%)" }}>
                                  {e.action === "applied" ? "Spontaneous application" : "Job offer not recorded"}
                                </Typography>
                              )
                            )}

                            {namesCandidate && (
                              e.properties?.candidate_id ? (
                                <Box
                                  component="button"
                                  type="button"
                                  onClick={() => navigate(`/internal/candidates/${e.properties.candidate_id}`)}
                                  sx={{
                                    mt: 0.25,
                                    p: 0,
                                    border: "none",
                                    background: "none",
                                    display: "block",
                                    textAlign: "left",
                                    cursor: "pointer",
                                    fontSize: 12.5,
                                    fontFamily: "Segoe UI Semibold, sans-serif",
                                    color: "hsl(150 38% 30%)",
                                    "&:hover": { textDecoration: "underline" },
                                    "&:focus-visible": { outline: "2px solid hsl(150 38% 30%)", outlineOffset: 2 },
                                  }}
                                >
                                  {e.properties.candidate_name || `Candidate #${e.properties.candidate_id}`}
                                </Box>
                              ) : (
                                <Typography sx={{ mt: 0.25, fontSize: 12.5, color: "hsl(150, 30%, 12%)" }}>
                                Unknown candidate
                                </Typography>
                              )
                            )}

                            {/* Non-search properties, so nothing tracked stays hidden */}
                            {!isSearch && !namesCandidate && !opensJob && e.properties && Object.keys(e.properties).length > 0 && (
                              <Stack direction="row" sx={{ mt: 0.5, gap: 0.5, flexWrap: "wrap" }}>
                                {Object.entries(e.properties).map(([k, v]) => (
                                  <Chip
                                    key={k}
                                    label={`${FILTER_LABELS[k] || titleize(k)}: ${typeof v === "object" ? JSON.stringify(v) : v}`}
                                    size="small"
                                    sx={{ height: 19, fontSize: 10.5, borderRadius: "999px", backgroundColor: "hsl(140 8% 93%)", color: "hsl(150, 10%, 46%)" }}
                                  />
                                ))}
                              </Stack>
                            )}
                          </Box>
                        </Box>
                      </React.Fragment>
                    );
                  })}
                </Box>
              ))
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

function chipSx(active, activeColor = "hsl(150, 38%, 17%)", activeBg = "hsl(150 25% 92%)") {
  return {
    height: 24,
    fontSize: 11.5,
    borderRadius: "999px",
    cursor: "pointer",
    fontFamily: "Segoe UI Semibold, sans-serif",
    backgroundColor: active ? activeBg : "hsl(140 8% 94%)",
    color: active ? activeColor : "hsl(150, 10%, 46%)",
    border: active ? `1px solid ${activeColor}` : "1px solid transparent",
    "&:hover": { backgroundColor: active ? activeBg : "hsl(140 8% 90%)" },
  };
}

export default FeatureActivityPanel;
